import { useState } from "react";
import { X, Plus, Users } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export default function EmailListEditor({ emails, onChange, disabled, testIdPrefix }) {
  const [input, setInput] = useState("");
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkText, setBulkText] = useState("");

  const addEmail = () => {
    const val = input.trim().toLowerCase();
    if (!val) return;
    if (emails.includes(val)) {
      setInput("");
      return;
    }
    onChange([...emails, val]);
    setInput("");
  };

  const removeEmail = (email) => {
    onChange(emails.filter((e) => e !== email));
  };

  const applyBulkImport = () => {
    const parsed = bulkText
      .split(/[\n,;]+/)
      .map((e) => e.trim().toLowerCase())
      .filter(Boolean);
    if (parsed.length === 0) {
      setBulkMode(false);
      return;
    }
    const merged = [...emails];
    for (const e of parsed) {
      if (!merged.includes(e)) merged.push(e);
    }
    onChange(merged);
    setBulkText("");
    setBulkMode(false);
  };

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap gap-1.5">
        {emails.length === 0 && <span className="text-xs text-muted-foreground italic">Aucun</span>}
        {emails.map((email) => (
          <span
            key={email}
            className="inline-flex items-center gap-1 bg-muted text-xs rounded-full px-2.5 py-1 text-foreground"
            data-testid={`${testIdPrefix}-chip-${email}`}
          >
            {email}
            {!disabled && (
              <button
                type="button"
                onClick={() => removeEmail(email)}
                className="hover:text-destructive"
                data-testid={`${testIdPrefix}-remove-${email}`}
                title="Retirer"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        ))}
      </div>

      {!disabled && !bulkMode && (
        <div className="flex gap-1.5">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addEmail();
              }
            }}
            placeholder="email@exemple.com"
            className="h-8 text-xs"
            data-testid={`${testIdPrefix}-input`}
          />
          <Button type="button" size="sm" variant="outline" className="h-8 px-2" onClick={addEmail} data-testid={`${testIdPrefix}-add-button`}>
            <Plus className="h-3.5 w-3.5" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 px-2"
            onClick={() => setBulkMode(true)}
            title="Importer plusieurs emails"
            data-testid={`${testIdPrefix}-bulk-toggle-button`}
          >
            <Users className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}

      {!disabled && bulkMode && (
        <div className="space-y-1.5">
          <Textarea
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            placeholder={"Collez une liste d'emails (séparés par virgule ou retour à la ligne)"}
            className="text-xs min-h-[70px]"
            data-testid={`${testIdPrefix}-bulk-textarea`}
          />
          <div className="flex gap-1.5">
            <Button type="button" size="sm" onClick={applyBulkImport} data-testid={`${testIdPrefix}-bulk-apply-button`}>
              Importer
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setBulkMode(false);
                setBulkText("");
              }}
              data-testid={`${testIdPrefix}-bulk-cancel-button`}
            >
              Annuler
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
